import type { Locale } from '../i18n'
import { AUTHOR } from '../lib/links'

/** Clé de catégorie stable, indépendante de la langue. */
export type CategoryKey =
  | 'all'
  | 'logistics'
  | 'ecommerce'
  | 'delivery'
  | 'technology'
  | 'sustainability'

/** Ordre d'affichage du filtre (inclut `all`). Labels via `blog.categories.<key>`. */
export const CATEGORY_KEYS: CategoryKey[] = [
  'all',
  'logistics',
  'ecommerce',
  'delivery',
  'technology',
  'sustainability',
]

/** Métadonnées localisées d'un article (par langue). */
export type ArticleI18n = {
  slug: string
  title: string
  /** Title tag SERP court si différent du H1 (`title`). Fallback : `title`. */
  seoTitle?: string
  excerpt: string
  /**
   * Q/R de la section FAQ du corps, dans l'ordre d'affichage. Si présent,
   * `BlogArticlePage` émet un FAQPage JSON-LD (rich results + réponses IA).
   * Les questions DOIVENT correspondre aux `<h3>` réellement rendus.
   */
  faq?: { q: string; a: string }[]
}

export type Article = {
  /** Identifiant stable = slug FR historique (= clé de ARTICLE_CONTENT). */
  id: string
  category: Exclude<CategoryKey, 'all'>
  readTime: number
  date: string
  /** Date de dernière modification (BCP-47 ISO). Si absent, retombe sur `date`. */
  dateModified?: string
  author: string
  cover: {
    palette: 'ink' | 'accent' | 'mixed'
    pattern: 'grid' | 'dots' | 'beam' | 'waves' | 'package'
  }
  featured?: boolean
  i18n: Record<Locale, ArticleI18n>
}

/**
 * Construit un Article à partir de ses métadonnées canoniques.
 * Les EN/NL sont initialisés comme COPIE du FR (placeholder traduit en Tasks 6-7),
 * sauf si des traductions explicites `en`/`nl` sont fournies (Tasks 6-7),
 * pour que /en et /nl rendent le FR au lieu de pages vides pendant Tasks 2-5.
 */
function makeArticle(input: {
  id: string
  category: Exclude<CategoryKey, 'all'>
  readTime: number
  date: string
  dateModified?: string
  /** Defaults to the named site author; only set to override on a guest post. */
  author?: string
  cover: Article['cover']
  featured?: boolean
  fr: ArticleI18n
  en?: ArticleI18n
  nl?: ArticleI18n
}): Article {
  const fr = input.fr
  const en: ArticleI18n = input.en ?? { ...fr }
  const nl: ArticleI18n = input.nl ?? { ...fr }
  return {
    id: input.id,
    category: input.category,
    readTime: input.readTime,
    date: input.date,
    dateModified: input.dateModified,
    author: input.author ?? AUTHOR.name,
    cover: input.cover,
    featured: input.featured,
    i18n: { fr, en, nl },
  }
}

export const articles: Article[] = [
  // ─────────── Dernier article publié ───────────
  makeArticle({
    id: 'vendre-aux-pays-bas-depuis-la-belgique',
    category: 'ecommerce',
    readTime: 11,
    date: '2026-10-05',
    cover: { palette: 'mixed', pattern: 'dots' },
    featured: true,
    fr: {
      slug: "vendre-aux-pays-bas-depuis-la-belgique",
      title:
        "Vendre aux Pays-Bas depuis la Belgique : TVA, emballages, bol et livraison en 2026",
      seoTitle: "Vendre aux Pays-Bas depuis la Belgique : guide 2026",
      excerpt:
        "Vendre aux Pays-Bas depuis la Belgique : TVA et OSS, emballages Verpact, iDEAL, bol et livraison J+1. Les règles 2026 à connaître avant de lancer.",
      faq: [
        {
          q: "Faut-il un numéro de TVA néerlandais pour vendre aux Pays-Bas depuis la Belgique ?",
          a: "Non, tant que vous expédiez depuis la Belgique. En dessous de 10 000 € de ventes B2C transfrontalières par an dans toute l'UE, vous facturez la TVA belge. Au-delà, vous facturez la TVA néerlandaise (21 % en taux normal, 9 % en taux réduit) mais vous la déclarez depuis la Belgique via le guichet OSS. En revanche, dès que votre stock se trouve aux Pays-Bas, y compris dans un entrepôt de place de marché, l'identification à la TVA néerlandaise est en principe requise.",
        },
        {
          q: "Faut-il s'enregistrer auprès de Verpact quand on vend aux Pays-Bas ?",
          a: "Seulement au-delà de 50 000 kg d'emballages mis sur le marché néerlandais par an, hors cas particulier des emballages plastique à usage unique. L'obligation vise aussi les vendeurs étrangers qui vendent en ligne à des consommateurs néerlandais. Depuis le 12 août 2026, la définition de producteur s'aligne sur le règlement européen sur les emballages, avec une période transitoire de pratique inchangée pour les emballages d'expédition. Pesez vos emballages pour pouvoir démontrer votre position.",
        },
        {
          q: "Peut-on vendre sur bol depuis la Belgique en expédiant depuis un entrepôt belge ?",
          a: "Oui. bol accepte les vendeurs belges, et vous pouvez expédier vous-même depuis la Belgique ou via votre prestataire, sans stocker aux Pays-Bas. Le modèle Logistiek via bol (LVB) place votre stock dans un entrepôt de bol aux Pays-Bas, ce qui déclenche en principe une identification à la TVA néerlandaise. Pour une boutique qui vend aussi sur son propre site, l'expédition depuis un stock unique est généralement la plus simple.",
        },
        {
          q: "Bancontact suffit-il pour vendre aux Pays-Bas ?",
          a: "Non. iDEAL représente 71 % des paiements en ligne néerlandais en 2025, et Bancontact est un moyen de paiement belge que les consommateurs néerlandais n'ont pas. iDEAL migre vers Wero, avec une migration qui doit s'achever au plus tard le 31 décembre 2027 : côté boutique, vérifiez simplement que votre prestataire de paiement affiche « iDEAL | Wero ».",
        },
        {
          q: "Combien de temps faut-il pour livrer les Pays-Bas depuis un entrepôt belge ?",
          a: "Selon le transporteur, comptez J+1 pour les commandes passées avant l'heure de cut-off. Chez Yaslan, toute commande reçue avant 15 h est expédiée le jour même depuis Willebroek. C'est la même promesse que celle d'un entrepôt néerlandais, ce qui est la raison pour laquelle un stock local ne se justifie presque jamais.",
        },
      ],
    },
    en: {
      slug: "selling-to-the-netherlands-from-belgium",
      title:
        "Selling to the Netherlands from Belgium: VAT, packaging, bol and delivery in 2026",
      seoTitle: "Selling to the Netherlands from Belgium: 2026 guide",
      excerpt:
        "Selling to the Netherlands from Belgium: VAT and OSS, Verpact packaging, iDEAL, bol and next-day delivery. The 2026 rules to know before you launch.",
      faq: [
        {
          q: "Do you need a Dutch VAT number to sell to the Netherlands from Belgium?",
          a: "No, as long as you ship from Belgium. Below €10,000 of cross-border B2C sales per year across the EU, you charge Belgian VAT. Above it, you charge Dutch VAT (21% standard rate, 9% reduced rate) but declare it from Belgium through the OSS scheme. However, as soon as your stock sits in the Netherlands, including in a marketplace warehouse, Dutch VAT registration is in principle required.",
        },
        {
          q: "Do you have to register with Verpact when selling to the Netherlands?",
          a: "Only above 50,000 kg of packaging placed on the Dutch market per year, apart from the special case of single-use plastic packaging. The obligation also targets foreign sellers who sell online to Dutch consumers. Since 12 August 2026, the definition of producer aligns with the EU packaging regulation, with a transitional period of unchanged practice for shipping packaging. Weigh your packaging so you can show where you stand.",
        },
        {
          q: "Can you sell on bol from Belgium while shipping from a Belgian warehouse?",
          a: "Yes. bol accepts Belgian sellers, and you can ship yourself from Belgium or through your provider, without storing in the Netherlands. The Logistics via bol (LVB) model places your stock in a bol warehouse in the Netherlands, which in principle triggers Dutch VAT registration. For a shop that also sells on its own site, shipping from a single stock is generally the simplest option.",
        },
        {
          q: "Is Bancontact enough to sell to the Netherlands?",
          a: "No. iDEAL accounted for 71% of Dutch online payments in 2025, and Bancontact is a Belgian payment method that Dutch consumers do not have. iDEAL is migrating to Wero, with the migration due to finish by 31 December 2027 at the latest: on the shop side, simply check that your payment provider displays \"iDEAL | Wero\".",
        },
        {
          q: "How long does it take to deliver the Netherlands from a Belgian warehouse?",
          a: "Depending on the carrier, count next day for orders placed before the cut-off time. At Yaslan, every order received before 3 pm is shipped the same day from Willebroek. That is the same promise as a Dutch warehouse, which is why a local stock is almost never justified.",
        },
      ],
    },
    nl: {
      slug: "verkopen-in-nederland-vanuit-belgie",
      title:
        "Verkopen in Nederland vanuit België: btw, verpakkingen, bol en levering in 2026",
      seoTitle: "Verkopen in Nederland vanuit België: de gids 2026",
      excerpt:
        "Verkopen in Nederland vanuit België: btw en OSS, Verpact-verpakkingen, iDEAL, bol en levering D+1. De regels van 2026 vóór je lanceert.",
      faq: [
        {
          q: "Heb je een Nederlands btw-nummer nodig om vanuit België in Nederland te verkopen?",
          a: "Nee, zolang je vanuit België verzendt. Onder € 10.000 grensoverschrijdende B2C-verkoop per jaar in de hele EU reken je Belgische btw aan. Erboven reken je Nederlandse btw aan (21% normaal tarief, 9% verlaagd tarief), maar geef je ze vanuit België aan via het OSS-éénloket. Zodra je voorraad echter in Nederland ligt, ook in een magazijn van een marktplaats, is de identificatie voor Nederlandse btw in principe vereist.",
        },
        {
          q: "Moet je je registreren bij Verpact als je in Nederland verkoopt?",
          a: "Alleen boven 50.000 kg verpakkingen per jaar op de Nederlandse markt, behalve het bijzondere geval van wegwerpplastic verpakkingen. De verplichting viseert ook buitenlandse verkopers die online aan Nederlandse consumenten verkopen. Sinds 12 augustus 2026 sluit de definitie van producent aan bij de Europese verpakkingsverordening, met een overgangsperiode van ongewijzigde praktijk voor verzendverpakkingen. Weeg je verpakkingen om te kunnen aantonen waar je staat.",
        },
        {
          q: "Kun je op bol verkopen vanuit België en verzenden vanuit een Belgisch magazijn?",
          a: "Ja. bol aanvaardt Belgische verkopers, en je kunt zelf vanuit België verzenden of via je dienstverlener, zonder in Nederland op te slaan. Het model Logistiek via bol (LVB) plaatst je voorraad in een magazijn van bol in Nederland, wat in principe de identificatie voor Nederlandse btw meebrengt. Voor een shop die ook op haar eigen site verkoopt, is verzenden vanuit één voorraad doorgaans de eenvoudigste optie.",
        },
        {
          q: "Volstaat Bancontact om in Nederland te verkopen?",
          a: "Nee. iDEAL was in 2025 goed voor 71% van de Nederlandse online betalingen, en Bancontact is een Belgisch betaalmiddel dat Nederlandse consumenten niet hebben. iDEAL migreert naar Wero, met een migratie die uiterlijk op 31 december 2027 moet zijn afgerond: controleer aan de kant van de shop gewoon of je betaalprovider \"iDEAL | Wero\" toont.",
        },
        {
          q: "Hoelang duurt een levering naar Nederland vanuit een Belgisch magazijn?",
          a: "Afhankelijk van de vervoerder: reken op D+1 voor bestellingen vóór het cut-offmoment. Bij Yaslan wordt elke bestelling die vóór 15 u binnenkomt, dezelfde dag vanuit Willebroek verzonden. Dat is dezelfde belofte als die van een Nederlands magazijn, en daarom is een lokale voorraad bijna nooit te verantwoorden.",
        },
      ],
    },
  }),

  makeArticle({
    id: 'supplements-haute-saison-2026',
    category: 'delivery',
    readTime: 10,
    date: '2026-09-09',
    cover: { palette: 'accent', pattern: 'package' },
    featured: true,
    fr: {
      slug: "supplements-haute-saison-2026",
      title:
        "Suppléments haute saison 2026 : ce que bpost, PostNL, DPD et DHL facturent entre Black Friday et Noël",
      seoTitle: "Suppléments haute saison 2026 : bpost, PostNL, DPD, DHL",
      excerpt:
        "Suppléments haute saison 2026 de bpost, PostNL, DPD, DHL et UPS : montants, dates, colis concernés, coût réel et six leviers pour les absorber.",
      faq: [
        {
          q: "Quel est le montant du supplément haute saison bpost en 2026 ?",
          a: "1 € hors TVA par colis, pour tous les clients professionnels, sur tous les colis remis entre le Black Friday et le jour de Noël. En 2026, la période court du vendredi 27 novembre à 6 h au vendredi 25 décembre à 6 h. Le supplément s'ajoute au tarif du colis et au supplément énergie de 0,14 €.",
        },
        {
          q: "Le supplément haute saison s'applique-t-il aussi aux retours ?",
          a: "Cela dépend du réseau. PostNL Belgique l'applique explicitement à tous les colis qui transitent par son réseau pendant la période, retours et envois internationaux compris. Chez bpost, la règle vise les colis remis pendant la fenêtre, quel que soit leur sens. Vérifiez vos conditions contractuelles et, surtout, le rapprochement de votre facture de janvier colis par colis.",
        },
        {
          q: "Peut-on éviter le supplément en changeant de transporteur ?",
          a: "Pas en l'évitant, en le réduisant. Chaque réseau a ses propres dates et son propre montant, du simple au triple pour des services comparables. Le gain vient de la répartition du volume entre plusieurs réseaux et de l'expédition anticipée avant les fenêtres, pas d'un changement de transporteur en pleine saison — qui vous mettrait dans les pires conditions tarifaires et opérationnelles de l'année.",
        },
        {
          q: "Le supplément est-il calculé sur la date de commande ou la date d'expédition ?",
          a: "Sur la date de remise du colis au transporteur. Une commande passée la veille de l'ouverture de la fenêtre mais remise le lendemain est surtaxée. C'est pour cette raison que l'heure de cut-off de votre entrepôt a un effet direct sur le coût de vos colis autour du 27 novembre.",
        },
        {
          q: "Un prestataire logistique absorbe-t-il le supplément haute saison ?",
          a: "Le supplément est fixé par le transporteur et s'applique à tous ses clients, prestataires compris. Ce qu'un prestataire apporte, c'est la capacité de router chaque colis vers le réseau le moins cher tout compris ce jour-là, des conditions négociées sur des volumes mutualisés, et l'absence de surcharge propre. Chez Yaslan, ni minimum mensuel ni surcharge saisonnière ne figurent sur la facture : la seule qui existe est celle du transporteur, et le routage sert à la minimiser.",
        },
      ],
    },
    en: {
      slug: "peak-season-surcharges-2026",
      title:
        "Peak season surcharges 2026: what bpost, PostNL, DPD and DHL charge between Black Friday and Christmas",
      seoTitle: "Peak season surcharges 2026: bpost, PostNL, DPD, DHL",
      excerpt:
        "Peak season surcharges 2026 from bpost, PostNL, DPD, DHL and UPS: amounts, dates, parcels concerned, real cost and six levers to absorb them.",
      faq: [
        {
          q: "What is the bpost peak season surcharge in 2026?",
          a: "€1 excl. VAT per parcel, for all business customers, on every parcel handed over between Black Friday and Christmas Day. In 2026 the period runs from Friday 27 November at 06:00 to Friday 25 December at 06:00. The surcharge comes on top of the parcel rate and the €0.14 energy surcharge.",
        },
        {
          q: "Does the peak season surcharge also apply to returns?",
          a: "It depends on the network. PostNL Belgium explicitly applies it to every parcel moving through its network during the period, returns and international shipments included. At bpost, the rule targets parcels handed over during the window, whichever direction they travel. Check your contractual terms and, above all, reconcile your January invoice parcel by parcel.",
        },
        {
          q: "Can you avoid the surcharge by switching carriers?",
          a: "Not by avoiding it, by reducing it. Every network has its own dates and its own amount, ranging from one to three times for comparable services. The gain comes from spreading volume across several networks and shipping early ahead of the windows, not from switching carriers mid-season — which would put you in the worst tariff and operational conditions of the year.",
        },
        {
          q: "Is the surcharge calculated on the order date or the shipping date?",
          a: "On the date the parcel is handed over to the carrier. An order placed the day before the window opens but handed over the next day is surcharged. That is why your warehouse's cut-off time has a direct effect on the cost of your parcels around 27 November.",
        },
        {
          q: "Does a logistics provider absorb the peak season surcharge?",
          a: "The surcharge is set by the carrier and applies to all its customers, providers included. What a provider brings is the ability to route each parcel to the cheapest all-in network that day, terms negotiated on pooled volumes, and no surcharge of its own. At Yaslan, neither a monthly minimum nor a seasonal surcharge appears on the invoice: the only one that exists is the carrier's, and routing is there to minimise it.",
        },
      ],
    },
    nl: {
      slug: "piektoeslagen-2026",
      title:
        "Piektoeslagen 2026: wat bpost, PostNL, DPD en DHL aanrekenen tussen Black Friday en Kerstmis",
      seoTitle: "Piektoeslagen 2026: bpost, PostNL, DPD, DHL",
      excerpt:
        "Piektoeslagen 2026 van bpost, PostNL, DPD, DHL en UPS: bedragen, data, betrokken pakketten, kost op een echt seizoen en zes hefbomen om ze op te vangen.",
      faq: [
        {
          q: "Hoeveel bedraagt de piektoeslag van bpost in 2026?",
          a: "€ 1 excl. btw per pakket, voor alle zakelijke klanten, op elk pakket dat tussen Black Friday en kerstdag wordt afgegeven. In 2026 loopt de periode van vrijdag 27 november om 6 u tot vrijdag 25 december om 6 u. De toeslag komt bovenop het pakkettarief en de energietoeslag van € 0,14.",
        },
        {
          q: "Geldt de piektoeslag ook voor retouren?",
          a: "Dat hangt af van het netwerk. PostNL België past ze uitdrukkelijk toe op alle pakketten die in de periode via zijn netwerk passeren, retouren en internationale zendingen inbegrepen. Bij bpost viseert de regel de pakketten die tijdens de periode worden afgegeven, ongeacht de richting. Kijk je contractuele voorwaarden na en stem vooral je factuur van januari pakket per pakket af.",
        },
        {
          q: "Kun je de toeslag vermijden door van vervoerder te veranderen?",
          a: "Niet vermijden, wel verminderen. Elk netwerk heeft zijn eigen data en zijn eigen bedrag, tot het drievoudige voor vergelijkbare diensten. De winst komt van de spreiding van het volume over meerdere netwerken en van vroeger verzenden vóór de periodes, niet van een vervoerderswissel midden in het seizoen — die je in de slechtste tarifaire en operationele omstandigheden van het jaar zou brengen.",
        },
        {
          q: "Wordt de toeslag berekend op de besteldatum of op de verzenddatum?",
          a: "Op de datum waarop het pakket aan de vervoerder wordt afgegeven. Een bestelling die de dag vóór de opening van de periode wordt geplaatst maar de dag erna wordt afgegeven, is belast. Daarom heeft het cut-offuur van je magazijn een rechtstreeks effect op de kost van je pakketten rond 27 november.",
        },
        {
          q: "Vangt een logistieke dienstverlener de piektoeslag op?",
          a: "De toeslag wordt door de vervoerder bepaald en geldt voor al zijn klanten, dienstverleners inbegrepen. Wat een dienstverlener wél biedt: elk pakket routeren naar het netwerk dat die dag all-in het goedkoopst is, voorwaarden onderhandeld op gebundelde volumes, en geen eigen toeslag. Bij Yaslan staat noch een maandelijks minimum, noch een seizoenstoeslag op de factuur: de enige die bestaat, is die van de vervoerder, en de routering dient om ze te minimaliseren.",
        },
      ],
    },
  }),

  makeArticle({
    id: 'vendre-en-france-depuis-la-belgique',
    category: 'ecommerce',
    readTime: 12,
    date: '2026-09-07',
    cover: { palette: 'ink', pattern: 'beam' },
    featured: true,
    fr: {
      slug: 'vendre-en-france-depuis-la-belgique',
      title:
        'Vendre en France depuis la Belgique : TVA, REP et livraison en 2026',
      seoTitle: 'Vendre en France depuis la Belgique : le guide 2026',
      excerpt:
        "Vendre en France depuis la Belgique : seuil TVA de 10 000 €, guichet OSS, mandataire REP obligatoire depuis juillet 2026, point relais et délais.",
      faq: [
        {
          q: "Faut-il un numéro de TVA français pour vendre en France depuis la Belgique ?",
          a: "Non, tant que vous expédiez depuis la Belgique. Au-delà de 10 000 € de ventes B2C transfrontalières par an, vous facturez la TVA française à 20 %, mais vous la déclarez depuis la Belgique via le guichet unique OSS, en une déclaration trimestrielle. En revanche, dès que vous détenez du stock sur le territoire français — entrepôt, prestataire local ou stock déporté chez une place de marché —, l'immatriculation à la TVA française devient obligatoire et l'OSS ne couvre plus ces ventes.",
        },
        {
          q: "Qu'est-ce que le mandataire REP et depuis quand est-il obligatoire ?",
          a: "C'est un représentant établi en France, désigné par mandat écrit, qui est subrogé dans vos obligations de responsabilité élargie du producteur : adhésion à l'éco-organisme, identifiant unique, déclarations et contributions. L'obligation résulte de l'article L. 541-10-9-1 du Code de l'environnement, créé par la loi n° 2026-602 du 8 juillet 2026 et en vigueur depuis le 10 juillet 2026. Elle vise tout producteur non établi en France, qu'il soit dans l'Union européenne ou en dehors, et couvre toutes les filières REP.",
        },
        {
          q: "Le seuil de 10 000 € s'applique-t-il par pays ou pour toute l'Union européenne ?",
          a: "Pour toute l'Union européenne. Le seuil est global et annuel : vos ventes B2C vers la France, les Pays-Bas, l'Allemagne et tous les autres États membres s'additionnent dans un compteur unique. Une boutique qui vend 4 000 € en France et 7 000 € aux Pays-Bas a dépassé le seuil, même si aucun pays pris isolément ne l'atteint. Le basculement s'applique dès la transaction qui franchit les 10 000 €.",
        },
        {
          q: "Qui paie l'éco-contribution : le e-commerçant ou son prestataire logistique ?",
          a: "Le e-commerçant. C'est vous qui mettez le produit sur le marché français, donc vous qui êtes producteur au sens de la REP : l'éco-contribution vous incombe, et le mandataire la verse en votre nom. Votre prestataire logistique intervient sur la donnée : c'est lui qui choisit les emballages d'expédition, qui en connaît le poids par matériau et qui peut vous fournir les tonnages réellement expédiés vers la France. Sans cette donnée, votre déclaration annuelle repose sur une estimation.",
        },
        {
          q: "Combien de temps faut-il pour livrer la France depuis un entrepôt belge ?",
          a: "Selon le transporteur, comptez J+1 sur le nord de la France et l'Île-de-France, et J+2 sur la moitié sud — pour des commandes passées avant l'heure de cut-off. Depuis Willebroek, la frontière est à moins de 130 km et Paris à environ 300 km, ce qui place la majorité de la population française dans la zone J+1. C'est la même promesse commerciale que la plupart des acteurs français sur la moitié nord du pays.",
        },
      ],
    },
    en: {
      slug: 'selling-to-france-from-belgium',
      title: 'Selling to France from Belgium: VAT, EPR and delivery in 2026',
      seoTitle: 'Selling to France from Belgium: the 2026 guide',
      excerpt:
        'Selling to France from Belgium: the €10,000 VAT threshold, the OSS scheme, the EPR representative mandatory since July 2026, pickup points and lead times.',
      faq: [
        {
          q: 'Do you need a French VAT number to sell to France from Belgium?',
          a: "No, as long as you ship from Belgium. Above €10,000 in cross-border B2C sales per year you charge French VAT at 20%, but you declare it from Belgium through the One Stop Shop, in a single quarterly return. However, as soon as you hold stock on French territory — a warehouse, a local provider or stock forward-deployed with a marketplace — French VAT registration becomes mandatory and the OSS scheme no longer covers those sales.",
        },
        {
          q: 'What is an EPR authorised representative and since when is it mandatory?',
          a: 'It is a representative established in France, appointed by written mandate, who is subrogated into your extended producer responsibility obligations: joining the producer responsibility organisation, the unique identifier, declarations and contributions. The obligation comes from article L. 541-10-9-1 of the French Environment Code, created by law no. 2026-602 of 8 July 2026 and in force since 10 July 2026. It applies to any producer not established in France, whether inside or outside the European Union, and covers every EPR waste stream.',
        },
        {
          q: 'Does the €10,000 threshold apply per country or across the whole European Union?',
          a: 'Across the whole European Union. The threshold is aggregated and annual: your B2C sales to France, the Netherlands, Germany and every other member state add up in a single counter. A shop selling €4,000 in France and €7,000 in the Netherlands has crossed it, even though no single country reaches it. The switch applies from the transaction that crosses €10,000.',
        },
        {
          q: 'Who pays the eco-contribution: the merchant or the logistics provider?',
          a: 'The merchant. You are the one placing the product on the French market, so you are the producer under EPR: the eco-contribution is yours, and the authorised representative pays it on your behalf. Your logistics provider comes in on the data side: they choose the shipping packaging, know its weight per material, and can give you the tonnage actually shipped to France. Without that data, your annual declaration rests on an estimate.',
        },
        {
          q: 'How long does it take to deliver France from a Belgian warehouse?',
          a: 'Depending on the carrier, count next day for northern France and the Paris region, and day+2 for the southern half — for orders placed before the cut-off time. From Willebroek the border is less than 130 km away and Paris around 300 km, which puts most of the French population in the next-day zone. That is the same commercial promise most French players make for the northern half of the country.',
        },
      ],
    },
    nl: {
      slug: 'verkopen-in-frankrijk-vanuit-belgie',
      title: 'Verkopen in Frankrijk vanuit België: btw, UPV en levering in 2026',
      seoTitle: 'Verkopen in Frankrijk vanuit België: de gids 2026',
      excerpt:
        'Verkopen in Frankrijk vanuit België: btw-drempel van € 10.000, OSS-loket, verplichte UPV-gemachtigde sinds juli 2026, afhaalpunten en levertijden.',
      faq: [
        {
          q: 'Heb je een Frans btw-nummer nodig om vanuit België in Frankrijk te verkopen?',
          a: 'Nee, zolang je vanuit België verzendt. Boven € 10.000 grensoverschrijdende B2C-verkoop per jaar reken je 20% Franse btw aan, maar geef je ze vanuit België aan via het OSS-éénloket, in één kwartaalaangifte. Zodra je echter voorraad op Frans grondgebied aanhoudt — een magazijn, een lokale dienstverlener of voorraad bij een marktplaats — wordt een Franse btw-registratie verplicht en dekt het OSS-loket die verkopen niet langer.',
        },
        {
          q: 'Wat is een UPV-gemachtigde en sinds wanneer is die verplicht?',
          a: 'Het is een in Frankrijk gevestigde vertegenwoordiger, aangesteld bij schriftelijk mandaat, die in jouw verplichtingen inzake uitgebreide producentenverantwoordelijkheid treedt: aansluiting bij het ecoorganisme, unieke identificatiecode, aangiften en bijdragen. De verplichting volgt uit artikel L. 541-10-9-1 van de Franse milieuwetgeving, ingevoerd door wet nr. 2026-602 van 8 juli 2026 en van kracht sinds 10 juli 2026. Ze geldt voor elke producent die niet in Frankrijk gevestigd is, binnen of buiten de Europese Unie, en dekt alle UPV-stromen.',
        },
        {
          q: 'Geldt de drempel van € 10.000 per land of voor de hele Europese Unie?',
          a: 'Voor de hele Europese Unie. De drempel is globaal en jaarlijks: je B2C-verkoop naar Frankrijk, Nederland, Duitsland en alle andere lidstaten telt op in één teller. Een webshop die € 4.000 in Frankrijk en € 7.000 in Nederland verkoopt, is erover — ook al haalt geen enkel land ze afzonderlijk. De omslag geldt vanaf de transactie die € 10.000 overschrijdt.',
        },
        {
          q: 'Wie betaalt de ecobijdrage: de webshop of de logistieke dienstverlener?',
          a: 'De webshop. Jij brengt het product op de Franse markt, dus jij bent producent in de zin van de UPV: de ecobijdrage komt jou toe, en de gemachtigde stort ze in jouw naam. Je logistieke dienstverlener komt tussen op het vlak van data: hij kiest de verzendverpakkingen, kent het gewicht per materiaal en kan je de werkelijk naar Frankrijk verzonden tonnages bezorgen. Zonder die data steunt je jaarlijkse aangifte op een schatting.',
        },
        {
          q: 'Hoelang duurt een levering naar Frankrijk vanuit een Belgisch magazijn?',
          a: 'Afhankelijk van de vervoerder: reken op D+1 voor Noord-Frankrijk en de regio Parijs, en D+2 voor de zuidelijke helft — voor bestellingen vóór het cut-offmoment. Vanuit Willebroek ligt de grens op minder dan 130 km en Parijs op ongeveer 300 km, waardoor het grootste deel van de Franse bevolking in de D+1-zone valt. Dat is dezelfde commerciële belofte als de meeste Franse spelers voor de noordelijke landshelft.',
        },
      ],
    },
  }),

  makeArticle({
    id: 'gestion-retours-ecommerce',
    category: 'logistics',
    readTime: 11,
    date: '2026-09-02',
    cover: { palette: 'mixed', pattern: 'waves' },
    featured: true,
    fr: {
      slug: 'gestion-retours-ecommerce',
      title:
        "Gestion des retours e-commerce : le coût réel et la vague de janvier",
      seoTitle: "Gestion des retours e-commerce : le coût réel",
      excerpt:
        "Gestion des retours e-commerce : les six postes de coût réels, le cadre légal belge (14 jours, qui paie le renvoi) et le plan pour absorber janvier.",
      faq: [
        {
          q: "Combien de temps un client belge a-t-il pour retourner une commande ?",
          a: "Deux délais s'enchaînent. Le client a 14 jours calendrier à partir de la réception du bien pour vous notifier sa rétractation, puis 14 jours supplémentaires pour renvoyer la marchandise. Entre la livraison et le retour physique du colis, il peut donc s'écouler près d'un mois. Si vous ne l'avez pas informé de ce droit avant la commande, le délai initial est prolongé de douze mois.",
        },
        {
          q: "Qui doit payer les frais de retour en Belgique ?",
          a: "Le consommateur supporte les frais directs de renvoi à une condition : que vous l'en ayez informé clairement avant la conclusion de la commande. Si vos conditions générales sont muettes sur ce point, les frais restent à votre charge. C'est l'erreur la plus fréquente et la plus facile à corriger : une phrase explicite, publiée avant la première commande de la saison.",
        },
        {
          q: "Dois-je rembourser les frais de livraison aller ?",
          a: "Oui. Le remboursement porte sur le prix de l'article et sur les frais de livraison aller, à hauteur du mode de livraison standard le moins cher que vous proposiez. Si le client a choisi une option express, le supplément reste à sa charge. Vous devez rembourser dans les 14 jours suivant la notification, mais vous pouvez attendre d'avoir reçu le bien ou la preuve de son expédition.",
        },
        {
          q: "Quels produits ne sont pas concernés par le droit de rétractation ?",
          a: "Principalement les biens scellés qui ne peuvent être renvoyés pour des raisons d'hygiène ou de santé une fois le scellé ouvert, les biens périssables, les biens personnalisés ou confectionnés sur mesure, et les biens mélangés de façon indissociable avec d'autres après livraison. L'exception d'hygiène ne vaut que si le scellé existe physiquement et se voit : elle se pose au conditionnement, pas dans vos conditions générales.",
        },
        {
          q: "Combien coûte réellement un retour e-commerce ?",
          a: "Six postes s'additionnent : le transport aller perdu, les frais de livraison à rembourser, le transport retour si vous le prenez en charge, la réception et le contrôle en entrepôt, le reconditionnement ou la perte de valeur, et le temps de service client. En temps opérateur, un retour traité correctement représente deux à trois fois une commande sortante — davantage encore si l'article ne repart pas en stock A.",
        },
        {
          q: "Quel taux de retour est normal dans mon secteur ?",
          a: "La mode et la chaussure se situent couramment entre 25 et 35 %, avec des pointes au-delà de 50 % sur certaines enseignes fast fashion en Allemagne et au Royaume-Uni. L'électronique tourne autour de 10 à 15 %. La cosmétique reste à un chiffre grâce à l'exception des biens scellés, et l'alimentaire est marginal. Tous secteurs confondus, plus de 17 % des ventes en ligne reviennent. Mais la moyenne sectorielle ne pilote rien : mesurez votre taux par référence.",
        },
        {
          q: "Pourquoi les retours arrivent-ils surtout en janvier ?",
          a: "Trois mécanismes s'additionnent : les deux délais légaux de 14 jours, qui repoussent mécaniquement un achat de fin novembre vers janvier ; les fenêtres de retour étendues au 31 janvier que beaucoup de boutiques offrent pour les cadeaux ; et le cadeau non désiré, qui n'est ouvert que le 25 décembre. Le pic de colis entrants tombe donc au moment où vos expéditions sont au plus bas.",
        },
      ],
    },
    en: {
      slug: 'ecommerce-returns-management',
      title:
        "E-commerce returns management: the real cost and the January wave",
      seoTitle: "E-commerce returns: the real cost and January",
      excerpt:
        "E-commerce returns management: the six real cost items, the Belgian legal frame (14 days, who pays the return) and the plan to absorb January.",
      faq: [
        {
          q: "How long does a Belgian customer have to return an order?",
          a: "Two deadlines run one after the other. The customer has 14 calendar days from receiving the goods to notify you of their withdrawal, then a further 14 days to send the goods back. So nearly a month can pass between delivery and the parcel physically coming back. If you did not inform them of that right before the order, the initial period is extended by twelve months.",
        },
        {
          q: "Who has to pay return shipping in Belgium?",
          a: "The consumer bears the direct cost of returning the goods on one condition: that you informed them clearly before the order was concluded. If your terms and conditions are silent on the point, the cost stays with you. It is the most common mistake and the easiest to fix: one explicit sentence, published before the first order of the season.",
        },
        {
          q: "Do I have to refund the outbound delivery cost?",
          a: "Yes. The refund covers the price of the item and the outbound delivery cost, up to the cheapest standard delivery option you offer. If the customer chose an express option, the supplement stays with them. You must refund within 14 days of the notification, but you may wait until you have received the goods or proof of dispatch.",
        },
        {
          q: "Which products are not covered by the right of withdrawal?",
          a: "Mainly sealed goods that cannot be returned for health or hygiene reasons once the seal is broken, perishable goods, personalised or made-to-measure goods, and goods inseparably mixed with others after delivery. The hygiene exception only holds if the seal physically exists and is visible: it is applied at packing, not in your terms and conditions.",
        },
        {
          q: "What does an e-commerce return really cost?",
          a: "Six items add up: the lost outbound shipping, the delivery cost to refund, the return shipping if you carry it, receiving and inspection in the warehouse, reconditioning or value loss, and customer-service time. In operator time, a properly handled return represents two to three times an outbound order — more still if the item does not go back into A-stock.",
        },
        {
          q: "What return rate is normal in my sector?",
          a: "Fashion and footwear commonly sit between 25 and 35 %, with peaks above 50 % at some fast-fashion retailers in Germany and the UK. Electronics runs around 10 to 15 %. Cosmetics stays in single digits thanks to the sealed-goods exception, and food is marginal. Across all sectors, more than 17 % of online sales come back. But the sector average steers nothing: measure your rate per SKU.",
        },
        {
          q: "Why do returns mostly arrive in January?",
          a: "Three mechanisms add up: the two 14-day legal deadlines, which mechanically push a late-November purchase into January; the return windows extended to 31 January that many shops offer for gifts; and the unwanted gift, which is only opened on 25 December. So the peak of inbound parcels falls exactly when your outbound shipping is at its lowest.",
        },
      ],
    },
    nl: {
      slug: 'retourbeheer-ecommerce',
      title:
        "Retourbeheer in e-commerce: de echte kost en de golf van januari",
      seoTitle: "Retourbeheer e-commerce: de echte kost en januari",
      excerpt:
        "Retourbeheer in e-commerce: de zes reële kostenposten, het Belgische kader (14 dagen, wie betaalt de retour) en het plan om januari op te vangen.",
      faq: [
        {
          q: "Hoelang heeft een Belgische klant om een bestelling terug te sturen?",
          a: "Twee termijnen volgen elkaar op. De klant heeft 14 kalenderdagen vanaf de ontvangst van het goed om zijn herroeping te melden, en daarna nog eens 14 dagen om de goederen terug te sturen. Tussen de levering en de fysieke terugkeer van het pakket kan dus bijna een maand verstrijken. Hebt u hem vóór de bestelling niet over dat recht geïnformeerd, dan wordt de oorspronkelijke termijn met twaalf maanden verlengd.",
        },
        {
          q: "Wie moet de retourkosten betalen in België?",
          a: "De consument draagt de directe kosten van terugzending op één voorwaarde : dat u hem daarover duidelijk hebt geïnformeerd vóór het sluiten van de bestelling. Zwijgen uw algemene voorwaarden daarover, dan blijven de kosten voor uw rekening. Het is de meest voorkomende en de makkelijkst te herstellen fout : één expliciete zin, gepubliceerd vóór de eerste bestelling van het seizoen.",
        },
        {
          q: "Moet ik de heenleveringskosten terugbetalen?",
          a: "Ja. De terugbetaling slaat op de prijs van het artikel en op de heenleveringskosten, ten belope van de goedkoopste standaardlevering die u aanbiedt. Koos de klant een expressoptie, dan blijft het supplement voor hem. U moet terugbetalen binnen 14 dagen na de melding, maar u mag wachten tot u het goed of het bewijs van verzending hebt ontvangen.",
        },
        {
          q: "Welke producten vallen niet onder het herroepingsrecht?",
          a: "Vooral verzegelde goederen die om hygiënische of gezondheidsredenen niet kunnen worden teruggestuurd zodra de verzegeling geopend is, bederfbare goederen, gepersonaliseerde of op maat gemaakte goederen, en goederen die na levering onlosmakelijk met andere vermengd zijn. De hygiëne-uitzondering geldt enkel als de verzegeling fysiek bestaat en zichtbaar is : ze wordt bij het conditioneren aangebracht, niet in uw algemene voorwaarden.",
        },
        {
          q: "Wat kost een e-commerceretour werkelijk?",
          a: "Zes posten tellen op : het verloren heentransport, de terug te betalen leveringskosten, het retourtransport als u het draagt, de ontvangst en controle in het magazijn, de herconditionering of het waardeverlies, en de tijd van de klantendienst. In operatortijd vraagt een correct verwerkte retour twee tot drie keer een uitgaande bestelling — meer nog als het artikel niet in stock A terugkeert.",
        },
        {
          q: "Welk retourpercentage is normaal in mijn sector?",
          a: "Mode en schoenen zitten doorgaans tussen 25 en 35 %, met uitschieters boven 50 % bij sommige fastfashionketens in Duitsland en het Verenigd Koninkrijk. Elektronica schommelt rond 10 tot 15 %. Cosmetica blijft in één cijfer dankzij de uitzondering voor verzegelde goederen, en voeding is marginaal. Alle sectoren samen keert meer dan 17 % van de onlineverkoop terug. Maar het sectorgemiddelde stuurt niets aan : meet uw percentage per referentie.",
        },
        {
          q: "Waarom komen retours vooral in januari binnen?",
          a: "Drie mechanismen tellen op : de twee wettelijke termijnen van 14 dagen, die een aankoop van eind november mechanisch naar januari duwen ; de tot 31 januari verlengde retourvensters die veel webshops voor cadeaus aanbieden ; en het ongewenste cadeau, dat pas op 25 december wordt geopend. De piek aan inkomende pakketten valt dus net wanneer uw verzendingen op hun laagst zijn.",
        },
      ],
    },
  }),
  makeArticle({
    id: 'changer-de-prestataire-logistique',
    category: 'logistics',
    readTime: 14,
    date: '2026-08-31',
    cover: { palette: 'ink', pattern: 'beam' },
    featured: true,
    fr: {
      slug: 'changer-de-prestataire-logistique',
      title:
        'Changer de prestataire logistique : migrer sans rupture avant le Q4',
      seoTitle: 'Changer de prestataire logistique en 8 semaines',
      excerpt:
        "Changer de prestataire logistique : le rétroplanning en 8 semaines, les clauses belges à relire avant de résilier et les 6 points où une migration casse.",
      faq: [
        {
          q: "Combien de temps faut-il pour changer de prestataire logistique ?",
          a: "Environ huit semaines entre la décision et la dernière commande expédiée par l'ancien entrepôt, plus deux semaines de consolidation. Le transport du stock ne représente que quelques jours ; le reste, c'est le nettoyage des données produit, l'inventaire contradictoire, les tests par canal et la période de chevauchement. Une migration en trois semaines est possible, mais elle se paie en écarts d'inventaire et en surventes.",
        },
        {
          q: "Peut-on changer de 3PL en pleine saison, juste avant le Black Friday ?",
          a: "C'est déconseillé. Le Black Friday 2026 tombe le 27 novembre : pour être serein, votre nouvel entrepôt doit traiter votre volume normal depuis au moins trois à quatre semaines, ce qui impose une bascule fin octobre au plus tard, donc une décision début septembre. Passé la mi-septembre, préparez la migration et basculez en janvier — volumes bas, retours absorbés, année pleine devant vous.",
        },
        {
          q: "Mon ancien prestataire peut-il retenir mon stock ?",
          a: "Oui, si vous lui devez de l'argent. La plupart des prestataires belges renvoient aux Conditions Générales Belges d'Expédition, qui leur accordent un privilège, un droit de rétention et un droit de gage sur les marchandises détenues — y compris pour une créance portant sur d'autres marchandises, et même si vous n'êtes pas propriétaire du stock. D'où la règle : réglez d'abord, contestez ensuite, et obtenez une confirmation écrite de mainlevée avant d'envoyer le camion.",
        },
        {
          q: "Quel préavis pour résilier un contrat logistique en Belgique ?",
          a: "Il n'existe pas de durée légale unique : c'est votre contrat qui fait foi. En pratique, trois mois avec reconduction tacite annuelle est la configuration la plus courante. Vérifiez trois points : la date à partir de laquelle le préavis court, la forme exigée (souvent le recommandé) et l'existence d'un volume minimum, qui détermine ce que vous paierez sur le préavis non travaillé.",
        },
        {
          q: "Combien coûte un changement de prestataire logistique ?",
          a: "Le coût ponctuel équivaut généralement à un à trois mois de facture logistique, réparti sur sept postes : préavis non travaillé, frais de sortie, transport inter-entrepôts, réception chez le nouveau prestataire, chevauchement de facturation, écarts d'inventaire et temps interne. Il s'amortit en six à douze mois si la nouvelle grille est réellement plus basse. Pour un écart de quelques centimes par commande, la migration n'est pas rentable.",
        },
        {
          q: "Comment éviter la survente pendant la bascule ?",
          a: "En n'ayant jamais deux entrepôts actifs sur le même canal. On remet le stock à zéro sur l'ancien connecteur, on vérifie la propagation sur chaque canal, puis seulement on ouvre le nouveau. Quelques heures de rupture affichée coûtent moins cher qu'une série d'annulations sur marketplace, qui dégradent durablement votre score vendeur.",
        },
        {
          q: "Que deviennent les retours et les étiquettes déjà envoyées ?",
          a: "Les étiquettes prépayées présentes dans les colis expédiés avant la bascule continuent de pointer vers l'ancienne adresse pendant plusieurs semaines. Faites écrire dans l'accord de sortie qui les réceptionne, qui les réexpédie et qui paie ce second transport. Prévoyez aussi le sort du portail de retours : s'il est hébergé par le prestataire sortant, il cesse de fonctionner à la fin du contrat.",
        },
      ],
    },
    en: {
      slug: 'switching-logistics-provider',
      title:
        'Switching logistics provider: migrating your stock without disruption before Q4',
      seoTitle: 'Switching logistics provider: the 8-week plan',
      excerpt:
        "Switching logistics provider: the 8-week plan, the Belgian clauses to re-read before you give notice, and the six points where a migration breaks.",
      faq: [
        {
          q: "How long does it take to switch logistics provider?",
          a: "About eight weeks between the decision and the last order shipped by the old warehouse, plus two weeks of consolidation. Transporting the stock only takes a few days; the rest is product-data clean-up, the joint stock count, per-channel testing and the overlap period. A three-week migration is possible, but you pay for it in stock discrepancies and overselling.",
        },
        {
          q: "Can you switch 3PL in peak season, just before Black Friday?",
          a: "It is not advisable. Black Friday 2026 falls on 27 November: to be safe, your new warehouse should have been handling your normal volume for at least three to four weeks, which means cutting over by late October at the latest, so deciding in early September. After mid-September, prepare the migration and cut over in January — low volumes, returns absorbed, a full year ahead of you.",
        },
        {
          q: "Can my old provider hold my stock?",
          a: "Yes, if you owe it money. Most Belgian providers refer to the General Belgian Forwarding Conditions, which grant them a privilege, a right of retention and a pledge over the goods they hold — including for a claim relating to other goods, and even if you are not the owner of the stock. Hence the rule: pay first, contest afterwards, and obtain written confirmation of release before sending the truck.",
        },
        {
          q: "What notice period applies to a logistics contract in Belgium?",
          a: "There is no single statutory duration: your contract governs. In practice, three months with automatic annual renewal is the most common setup. Check three things: the date from which notice runs, the required form (often registered mail) and whether a minimum volume exists, since that determines what you pay on unworked notice.",
        },
        {
          q: "How much does switching logistics provider cost?",
          a: "The one-off cost typically equals one to three months of logistics invoicing, spread across seven items: unworked notice, exit fees, inter-warehouse transport, receiving at the new provider, billing overlap, stock discrepancies and internal time. It amortises in six to twelve months if the new rate card is genuinely lower. For a gap of a few cents per order, migrating is not worth it.",
        },
        {
          q: "How do you avoid overselling during the cutover?",
          a: "By never having two warehouses live on the same channel. Zero the stock on the old connector, verify propagation on every channel, and only then open the new one. A few hours showing out-of-stock costs less than a run of marketplace cancellations, which durably damage your seller score.",
        },
        {
          q: "What happens to returns and labels already sent out?",
          a: "Prepaid labels inside parcels shipped before the cutover keep pointing at the old address for several weeks. Have the exit agreement state who receives them, who forwards them and who pays that second leg. Also settle the fate of the returns portal: if the outgoing provider hosts it, it stops working when the contract ends.",
        },
      ],
    },
    nl: {
      slug: 'veranderen-van-logistieke-dienstverlener',
      title:
        'Veranderen van logistieke dienstverlener: migreren zonder onderbreking vóór het Q4',
      seoTitle: 'Veranderen van logistieke dienstverlener in 8 weken',
      excerpt:
        "Veranderen van logistieke dienstverlener: de planning in 8 weken, de clausules om te herlezen vóór u opzegt en de zes breekpunten van een migratie.",
      faq: [
        {
          q: "Hoelang duurt het om van logistieke dienstverlener te veranderen?",
          a: "Ongeveer acht weken tussen de beslissing en de laatste order die het oude magazijn verstuurt, plus twee weken consolidatie. Het transport van de voorraad neemt maar enkele dagen in beslag; de rest is het opschonen van de productdata, de tegensprekelijke telling, het testen per kanaal en de overlapperiode. Een migratie in drie weken kan, maar u betaalt ze in voorraadverschillen en oververkoop.",
        },
        {
          q: "Kan je van 3PL veranderen in volle piek, vlak voor Black Friday?",
          a: "Dat is af te raden. Black Friday 2026 valt op 27 november: om gerust te zijn moet uw nieuwe magazijn uw normale volume al drie tot vier weken verwerken, wat een omschakeling ten laatste eind oktober oplegt en dus een beslissing begin september. Na half september bereidt u de migratie voor en schakelt u in januari om — lage volumes, retours verwerkt, een vol jaar voor de boeg.",
        },
        {
          q: "Kan mijn oude dienstverlener mijn voorraad vasthouden?",
          a: "Ja, als u hem geld verschuldigd bent. De meeste Belgische dienstverleners verwijzen naar de Algemene Belgische Expeditievoorwaarden, die hun een voorrecht, een retentierecht en een pandrecht geven op de goederen die zij onder zich hebben — ook voor een schuldvordering die op andere goederen slaat, en zelfs als u geen eigenaar bent van de voorraad. Vandaar de regel: eerst betalen, daarna betwisten, en een schriftelijke bevestiging van vrijgave vragen vóór u de vrachtwagen stuurt.",
        },
        {
          q: "Welke opzegtermijn geldt voor een logistiek contract in België?",
          a: "Er bestaat geen enkele wettelijke duur: uw contract is bepalend. In de praktijk is drie maanden met stilzwijgende jaarlijkse verlenging de meest voorkomende opzet. Controleer drie zaken: vanaf welke datum de opzeg loopt, de vereiste vorm (vaak aangetekend) en of er een minimumvolume bestaat, want dat bepaalt wat u op de niet-gepresteerde opzeg betaalt.",
        },
        {
          q: "Wat kost het om van logistieke dienstverlener te veranderen?",
          a: "De eenmalige kost komt doorgaans neer op één tot drie maanden logistieke facturatie, verdeeld over zeven posten: niet-gepresteerde opzeg, uitstapkosten, transport tussen magazijnen, ontvangst bij de nieuwe dienstverlener, overlappende facturatie, voorraadverschillen en interne tijd. Ze wordt in zes tot twaalf maanden afgeschreven als het nieuwe tarief werkelijk lager ligt. Voor een verschil van enkele centen per order is migreren niet rendabel.",
        },
        {
          q: "Hoe vermijdt u oververkoop tijdens de omschakeling?",
          a: "Door nooit twee magazijnen tegelijk actief te hebben op hetzelfde kanaal. Zet de voorraad op nul op de oude koppeling, controleer de doorstroming op elk kanaal, en open pas daarna de nieuwe. Enkele uren « uitverkocht » tonen kost minder dan een reeks annulaties op een marktplaats, die uw verkopersscore blijvend aantasten.",
        },
        {
          q: "Wat gebeurt er met retours en labels die al verstuurd zijn?",
          a: "Voorafbetaalde labels in pakketten die vóór de omschakeling vertrokken, blijven weken naar het oude adres wijzen. Laat in de uitstapovereenkomst opnemen wie ze ontvangt, wie ze doorstuurt en wie dat tweede transport betaalt. Regel ook het lot van het retourportaal: host de vertrekkende dienstverlener het, dan stopt het bij het einde van het contract.",
        },
      ],
    },
  }),

  makeArticle({
    id: 'e-fulfillment-belgique',
    category: 'logistics',
    readTime: 12,
    date: '2026-08-24',
    cover: { palette: 'accent', pattern: 'grid' },
    featured: true,
    fr: {
      slug: 'e-fulfillment-belgique',
      title: 'E-fulfillment en Belgique : quel prestataire pour quel volume ?',
      seoTitle: 'E-fulfillment Belgique : guide 2026 des prestataires',
      excerpt:
        "E-fulfillment en Belgique : où sont les entrepôts, quels prestataires acceptent quels volumes, combien ça coûte vraiment et ce que la douane 2026 change.",
      faq: [
        {
          q: "Quelle différence entre e-fulfillment, 3PL et logistique e-commerce ?",
          a: "Aucune en pratique. « 3PL » désigne le prestataire, « e-fulfillment » la prestation appliquée aux commandes en ligne, « logistique e-commerce » est le terme générique français. Si vous voulez le détail des étapes, notre guide qu'est-ce que l'e-fulfillment le déroule.",
        },
        {
          q: "À partir de combien de commandes faut-il externaliser sa logistique e-commerce ?",
          a: "En dessous de 50 commandes par mois, préparer soi-même reste presque toujours plus rentable. Entre 50 et 200, le calcul dépend de la valeur de votre temps et de votre local. Au-delà de 200, l'externalisation devient difficile à battre — surtout si vous payez un entrepôt et des heures de préparation.",
        },
        {
          q: "Pourquoi choisir la Belgique plutôt que les Pays-Bas ou la France pour son entrepôt e-commerce ?",
          a: "Pour la position. Un rayon de 500 km autour de l'axe Anvers–Bruxelles couvre Paris, Amsterdam, Lille, Cologne, le Luxembourg et la Ruhr. Ajoutez le port d'Anvers pour vos imports et un réseau de transporteurs dense (bpost, DHL, GLS, UPS, DPD, PostNL). Pour un marché principalement français, un entrepôt du nord de la France peut être plus pertinent ; pour un marché européen, la Belgique est difficile à battre.",
        },
        {
          q: "Un prestataire peut-il gérer Shopify et Amazon depuis le même stock ?",
          a: "Oui, c'est même la raison numéro un d'externaliser chez les vendeurs multi-canaux. Vérifiez que la synchronisation est bidirectionnelle et en temps réel, sinon vous survendrez.",
        },
        {
          q: "La taxe de 3 € sur les petits colis s'applique-t-elle à un stock importé en gros ?",
          a: "Non. Le forfait vise les envois de moins de 150 € expédiés directement à un consommateur. Une palette ou un conteneur suit le régime douanier commercial classique : droits calculés sur la valeur réelle, payés une fois au dédouanement. Ensuite, vos expéditions depuis la Belgique sont des envois intra-européens.",
        },
        {
          q: "Combien de temps prend une migration vers un nouveau prestataire d'e-fulfillment ?",
          a: "Deux à quatre semaines pour l'exécution une fois le contrat signé, dont l'essentiel est le transport physique de votre stock — la partie technique (connexion de la boutique, paramétrage) se compte en heures. Comptez en revanche huit semaines depuis la décision si vous y incluez le préavis, la reprise des données produit et l'inventaire contradictoire.",
        },
        {
          q: "Mes clients verront-ils le nom du prestataire logistique ?",
          a: "Chez les acteurs sérieux, non : emballage à votre marque, bon de livraison à votre en-tête, adresse expéditeur à votre nom. Posez explicitement la question du portail de retour, qui est l'endroit où la marque blanche saute le plus souvent.",
        },
      ],
    },
    en: {
      slug: 'e-fulfillment-belgium',
      title: 'E-fulfillment in Belgium: which provider for which volume?',
      seoTitle: 'E-fulfillment Belgium: the 2026 provider guide',
      excerpt:
        'E-fulfillment in Belgium: where the warehouses are, which providers take which volumes, what it really costs and what 2026 customs rules changed.',
      faq: [
        {
          q: "What is the difference between e-fulfillment, 3PL and e-commerce logistics?",
          a: "None in practice. \"3PL\" names the provider, \"e-fulfillment\" the service applied to online orders, and \"e-commerce logistics\" is the generic term. If you want the step-by-step detail, our guide on what e-fulfillment is walks through it.",
        },
        {
          q: "At how many orders should you outsource e-commerce logistics?",
          a: "Under 50 orders a month, picking yourself is almost always more profitable. Between 50 and 200, the calculation depends on the value of your time and your premises. Above 200, outsourcing becomes hard to beat — especially if you are paying for a warehouse and picking hours.",
        },
        {
          q: "Why choose Belgium rather than the Netherlands or France for an e-commerce warehouse?",
          a: "For the position. A 500 km radius around the Antwerp–Brussels corridor covers Paris, Amsterdam, Lille, Cologne, Luxembourg and the Ruhr. Add the port of Antwerp for your imports and a dense carrier network (bpost, DHL, GLS, UPS, DPD, PostNL). For a market that is mostly French, a warehouse in northern France may be more relevant; for a European market, Belgium is hard to beat.",
        },
        {
          q: "Can one provider handle Shopify and Amazon from the same stock?",
          a: "Yes — it is the number one reason multi-channel sellers outsource. Check that the sync is bidirectional and real-time, otherwise you will oversell.",
        },
        {
          q: "Does the €3 small-parcel tax apply to stock imported in bulk?",
          a: "No. The flat fee targets consignments under €150 shipped directly to a consumer. A pallet or a container follows the standard commercial customs regime: duty calculated on the real value, paid once at clearance. After that, your shipments from Belgium are intra-European consignments.",
        },
        {
          q: "How long does a migration to a new e-fulfillment provider take?",
          a: "Two to four weeks for execution once the contract is signed, most of which is the physical transport of your stock — the technical side (connecting the store, configuration) is measured in hours. Count eight weeks from the decision, though, if you include notice, product-data clean-up and the joint stock count.",
        },
        {
          q: "Will my customers see the logistics provider's name?",
          a: "With serious players, no: packaging in your brand, delivery note on your letterhead, sender address in your name. Ask explicitly about the returns portal — that is where white labelling most often breaks down.",
        },
      ],
    },
    nl: {
      slug: 'e-fulfilment-belgie',
      title: 'E-fulfilment in België: welke dienstverlener voor welk volume?',
      seoTitle: 'E-fulfilment België: de providergids 2026',
      excerpt:
        'E-fulfilment in België: waar de magazijnen liggen, welke dienstverleners welke volumes aannemen, wat het echt kost en wat de douane 2026 verandert.',
      faq: [
        {
          q: "Wat is het verschil tussen e-fulfilment, 3PL en e-commercelogistiek?",
          a: "In de praktijk geen. \"3PL\" benoemt de dienstverlener, \"e-fulfilment\" de dienst toegepast op online orders, en \"e-commercelogistiek\" is de algemene term. Wilt u de stappen in detail, dan overloopt onze gids wat e-fulfilment is ze een voor een.",
        },
        {
          q: "Vanaf hoeveel orders moet u uw e-commercelogistiek uitbesteden?",
          a: "Onder 50 orders per maand blijft zelf klaarmaken bijna altijd rendabeler. Tussen 50 en 200 hangt de berekening af van de waarde van uw tijd en van uw pand. Boven 200 wordt uitbesteden moeilijk te kloppen — zeker als u een magazijn en verwerkingsuren betaalt.",
        },
        {
          q: "Waarom België kiezen in plaats van Nederland of Frankrijk voor een e-commercemagazijn?",
          a: "Om de ligging. Een straal van 500 km rond de as Antwerpen–Brussel dekt Parijs, Amsterdam, Rijsel, Keulen, Luxemburg en het Ruhrgebied. Voeg daar de haven van Antwerpen voor uw import aan toe en een dicht vervoerdersnetwerk (bpost, DHL, GLS, UPS, DPD, PostNL). Voor een overwegend Franse markt kan een magazijn in Noord-Frankrijk relevanter zijn; voor een Europese markt is België moeilijk te kloppen.",
        },
        {
          q: "Kan één dienstverlener Shopify en Amazon vanuit dezelfde voorraad beheren?",
          a: "Ja, en het is zelfs de belangrijkste reden waarom multichannelverkopers uitbesteden. Controleer wel of de synchronisatie tweerichtings en realtime is, anders verkoopt u meer dan u hebt.",
        },
        {
          q: "Geldt de taks van 3 € op kleine pakketten ook voor in bulk ingevoerde voorraad?",
          a: "Nee. Het forfait viseert zendingen van minder dan 150 € die rechtstreeks naar een consument gaan. Een pallet of een container volgt het klassieke commerciële douaneregime: rechten berekend op de werkelijke waarde en één keer betaald bij inklaring. Daarna zijn uw zendingen vanuit België intra-Europese zendingen.",
        },
        {
          q: "Hoelang duurt een migratie naar een nieuwe e-fulfilmentpartner?",
          a: "Twee tot vier weken voor de uitvoering zodra het contract getekend is, waarvan het grootste deel het fysieke transport van uw voorraad is — het technische gedeelte (koppeling van de webshop, instellingen) telt in uren. Reken echter op acht weken vanaf de beslissing als u de opzeg, het opschonen van de productdata en de tegensprekelijke telling meetelt.",
        },
        {
          q: "Zien mijn klanten de naam van de logistieke dienstverlener?",
          a: "Bij serieuze spelers niet: verpakking in uw merk, leveringsbon met uw briefhoofd, afzenderadres op uw naam. Stel uitdrukkelijk de vraag over het retourportaal — daar sneuvelt de white label het vaakst.",
        },
      ],
    },
  }),

  makeArticle({
    id: 'vendre-royaume-uni-suisse-depuis-belgique',
    category: 'ecommerce',
    readTime: 7,
    date: '2026-07-23',
    cover: { palette: 'mixed', pattern: 'waves' },
    featured: true,
    fr: {
      slug: 'vendre-royaume-uni-suisse-depuis-belgique',
      title:
        'Vendre au Royaume-Uni et en Suisse depuis la Belgique : le guide 2026',
      seoTitle: 'Vendre au Royaume-Uni & en Suisse depuis la Belgique',
      excerpt:
        'Vendre au Royaume-Uni et en Suisse depuis la Belgique : TVA, seuil 135 £, TVA suisse 8,1 %, douane, EORI, DDP vs DAP. Le guide pratique 2026.',
    },
    en: {
      slug: 'sell-uk-switzerland-from-belgium',
      title: 'Selling to the UK and Switzerland from Belgium: the 2026 guide',
      seoTitle: 'Sell to the UK & Switzerland from Belgium',
      excerpt:
        'Selling to the UK and Switzerland from Belgium: VAT, £135 threshold, 8.1% Swiss VAT, customs, EORI, DDP vs DAP. The practical 2026 guide.',
    },
    nl: {
      slug: 'verkopen-vk-zwitserland-vanuit-belgie',
      title: 'Verkopen aan het VK en Zwitserland vanuit België: de gids 2026',
      seoTitle: 'Verkopen aan het VK & Zwitserland vanuit België',
      excerpt:
        'Verkopen aan het VK en Zwitserland vanuit België: btw, £135-drempel, 8,1% Zwitserse btw, douane, EORI, DDP vs DAP. De praktische gids 2026.',
    },
  }),

  makeArticle({
    id: 'taxe-3-euros-petits-colis-chine',
    category: 'ecommerce',
    readTime: 7,
    date: '2026-07-14',
    cover: { palette: 'ink', pattern: 'package' },
    fr: {
      slug: 'taxe-3-euros-petits-colis-chine',
      title:
        'Taxe de 3 € sur les petits colis importés de Chine : ce qui change pour votre e-commerce',
      seoTitle: 'Taxe de 3 € sur les colis de Chine : guide e-commerce',
      excerpt:
        "Taxe de 3 € par catégorie d'articles sur les petits colis importés depuis juillet 2026 : calcul, impact sur vos marges et comment adapter votre logistique.",
    },
    en: {
      slug: 'eu-3-euro-tax-small-parcels-china',
      title:
        'The €3 tax on small parcels imported from China: what changes for your e-commerce',
      seoTitle: 'EU €3 tax on parcels from China: e-commerce guide',
      excerpt:
        'A €3 duty per product category on small parcels imported into the EU since July 2026: calculation, margin impact and how to adapt your logistics.',
    },
    nl: {
      slug: 'taks-3-euro-kleine-pakketten-china',
      title:
        'De taks van 3 euro op kleine pakketten uit China: wat er verandert voor je e-commerce',
      seoTitle: 'Taks van 3 euro op pakketten uit China: e-commercegids',
      excerpt:
        'Een taks van 3 euro per artikelcategorie op kleine importpakketten sinds juli 2026: berekening, impact op je marges en hoe je je logistiek aanpast.',
    },
  }),

  makeArticle({
    id: 'logistique-ecommerce-belgique-pourquoi-yaslan',
    category: 'logistics',
    readTime: 6,
    date: '2026-07-03',
    cover: { palette: 'mixed', pattern: 'beam' },
    featured: true,
    fr: {
      slug: 'logistique-ecommerce-belgique-pourquoi-yaslan',
      title: 'Logistique e-commerce en Belgique : pourquoi choisir Yaslan',
      excerpt:
        "La Belgique est un hub logistique majeur en Europe. Découvrez pourquoi y externaliser votre logistique e-commerce — et comment Yaslan s'occupe de tout, de A à Z.",
    },
    en: {
      slug: 'ecommerce-logistics-belgium-why-choose-yaslan',
      title: 'E-commerce logistics in Belgium: why choose Yaslan',
      excerpt:
        "Belgium is one of Europe's major logistics hubs. Discover why to outsource your e-commerce logistics here — and how Yaslan handles everything, from A to Z.",
    },
    nl: {
      slug: 'ecommerce-logistiek-belgie-waarom-yaslan-kiezen',
      title: 'E-commercelogistiek in België: waarom kiezen voor Yaslan',
      excerpt:
        "België is een van de belangrijkste logistieke hubs van Europa. Ontdek waarom je je e-commercelogistiek hier het best uitbesteedt — en hoe Yaslan alles van A tot Z voor je regelt.",
    },
  }),

  makeArticle({
    id: 'hausses-tarifaires-2026-marges-ecommerce',
    category: 'logistics',
    readTime: 7,
    date: '2026-05-20',
    cover: { palette: 'mixed', pattern: 'waves' },
    featured: true,
    fr: {
      slug: 'hausses-tarifaires-2026-marges-ecommerce',
      title:
        'Hausses tarifaires 2026 : protéger les marges de votre e-commerce',
      excerpt:
        "Bpost +6 %, taxe douanière 3 € sur les petits colis, exigences clients accrues : voici les leviers concrets pour protéger vos marges e-commerce en 2026 sans sacrifier les délais.",
    },
    en: {
      slug: '2026-price-increases-protect-ecommerce-margins',
      title: '2026 rate increases: protecting your e-commerce margins',
      excerpt:
        'bpost +6 %, a 3 € customs duty on small parcels, rising customer expectations: here are the concrete levers to protect your e-commerce margins in 2026 without sacrificing delivery times.',
    },
    nl: {
      slug: 'prijsstijgingen-2026-bescherm-je-ecommerce-marges',
      title: 'Prijsstijgingen 2026: bescherm de marges van je e-commerce',
      excerpt:
        'bpost +6 %, een douanerecht van 3 € op kleine pakketten, hogere klantverwachtingen: dit zijn de concrete hefbomen om je e-commercemarges in 2026 te beschermen zonder in te boeten op de termijnen.',
    },
  }),

  // ─────────── 2 nouveaux articles SEO ───────────
  makeArticle({
    id: 'black-friday-2026-preparer-logistique',
    category: 'logistics',
    readTime: 8,
    date: '2026-04-25',
    cover: { palette: 'accent', pattern: 'beam' },
    featured: true,
    fr: {
      slug: 'black-friday-2026-preparer-logistique',
      title: 'Black Friday 2026 : préparer votre logistique e-commerce en 6 étapes',
      excerpt:
        "Pic de volumes ×3 à ×10, transporteurs saturés, retours qui explosent en janvier. Voici comment préparer votre logistique e-commerce 60 jours avant Black Friday pour transformer le pic en croissance, pas en crise.",
    },
    en: {
      slug: 'black-friday-2026-prepare-ecommerce-logistics',
      title: 'Black Friday 2026: prepare your e-commerce logistics in 6 steps',
      excerpt:
        'Volume peaks of ×3 to ×10, saturated carriers, returns exploding in January. Here is how to prepare your e-commerce logistics 60 days before Black Friday to turn the peak into growth, not a crisis.',
    },
    nl: {
      slug: 'black-friday-2026-ecommerce-logistiek-voorbereiden',
      title: 'Black Friday 2026: je e-commercelogistiek voorbereiden in 6 stappen',
      excerpt:
        'Volumepieken van ×3 tot ×10, verzadigde vervoerders, retourzendingen die in januari exploderen. Zo bereid je je e-commercelogistiek 60 dagen vóór Black Friday voor om de piek in groei te veranderen, niet in een crisis.',
    },
  }),
  makeArticle({
    id: 'cut-off-15h-conversion-ecommerce',
    category: 'delivery',
    readTime: 6,
    date: '2026-04-22',
    cover: { palette: 'ink', pattern: 'grid' },
    fr: {
      slug: 'cut-off-15h-conversion-ecommerce',
      title: "Cut-off à 15h : ce que cela change vraiment pour votre conversion e-commerce",
      excerpt:
        "Le cut-off de préparation est l'un des leviers de conversion les plus sous-estimés en e-commerce. Décryptage : pourquoi 15h n'est pas qu'une heure, mais une promesse client mesurable — qui peut faire passer votre taux d'abandon de panier de 73 % à 55 %.",
    },
    en: {
      slug: '15h-cut-off-ecommerce-conversion',
      title: '15:00 cut-off: what it really changes for your e-commerce conversion',
      excerpt:
        'The fulfilment cut-off is one of the most underestimated conversion levers in e-commerce. A breakdown: why 15:00 is not just a time, but a measurable customer promise — one that can take your basket abandonment rate from 73 % to 55 %.',
    },
    nl: {
      slug: 'cut-off-15u-ecommerce-conversie',
      title: 'Cut-off om 15u: wat het echt verandert voor je e-commerceconversie',
      excerpt:
        'De verwerkings-cut-off is een van de meest onderschatte conversiehefbomen in e-commerce. Een analyse: waarom 15u niet zomaar een uur is, maar een meetbare klantbelofte — die je percentage verlaten winkelmandjes van 73 % naar 55 % kan brengen.',
    },
  }),

  // ─────────── Articles existants (CSV) ───────────
  makeArticle({
    id: 'logistique-multi-canal-amazon-shopify-woocommerce-belgique',
    category: 'ecommerce',
    readTime: 9,
    date: '2026-04-14',
    cover: { palette: 'accent', pattern: 'grid' },
    fr: {
      slug: 'logistique-multi-canal-amazon-shopify-woocommerce-belgique',
      title:
        'Logistique multi-canal en Belgique : gérer Amazon, Shopify et WooCommerce depuis un seul entrepôt 3PL',
      excerpt:
        "Vous vendez sur Amazon, Shopify, WooCommerce ou Bol.com en même temps ? Découvrez comment centraliser toute votre logistique multi-canal depuis un seul entrepôt 3PL en Belgique, synchroniser vos stocks en temps réel et expédier sous 24 h — sans engagement.",
    },
    en: {
      slug: 'multi-channel-logistics-amazon-shopify-woocommerce-belgium',
      title:
        'Multi-channel logistics in Belgium: managing Amazon, Shopify and WooCommerce from a single 3PL warehouse',
      excerpt:
        'Do you sell on Amazon, Shopify, WooCommerce or Bol.com at the same time? Discover how to centralise your entire multi-channel logistics from a single 3PL warehouse in Belgium, synchronise your stock in real time and ship within 24h — with no commitment.',
    },
    nl: {
      slug: 'multichannel-logistiek-amazon-shopify-woocommerce-belgie',
      title:
        'Multichannel-logistiek in België: Amazon, Shopify en WooCommerce beheren vanuit één 3PL-magazijn',
      excerpt:
        'Verkoop je tegelijk op Amazon, Shopify, WooCommerce of Bol.com? Ontdek hoe je je volledige multichannel-logistiek centraliseert vanuit één 3PL-magazijn in België, je voorraad in realtime synchroniseert en binnen 24u verzendt — zonder verbintenis.',
    },
  }),
  makeArticle({
    id: 'logistique-alimentaire-ecommerce-afsca-belgique',
    category: 'delivery',
    readTime: 8,
    date: '2026-04-14',
    cover: { palette: 'mixed', pattern: 'package' },
    fr: {
      slug: 'logistique-alimentaire-ecommerce-afsca-belgique',
      title:
        "Logistique alimentaire e-commerce en Belgique : tout ce qu'il faut savoir sur l'AFSCA",
      excerpt:
        "Vous vendez des produits alimentaires en ligne en Belgique ? Découvrez les obligations AFSCA, les règles HACCP et comment un prestataire 3PL agréé à Willebroek peut gérer votre logistique alimentaire en toute conformité.",
    },
    en: {
      slug: 'food-ecommerce-logistics-belgium-afsca-guide',
      title:
        'Food e-commerce logistics in Belgium: the complete AFSCA guide',
      excerpt:
        'Selling food products online in Belgium? Discover AFSCA obligations, HACCP rules and how an approved 3PL in Willebroek can manage your food e-commerce logistics in full compliance.',
    },
    nl: {
      slug: 'voedingslogistiek-ecommerce-belgie-favv-gids',
      title:
        'Voedingslogistiek e-commerce in België: de complete FAVV-gids',
      excerpt:
        'Verkoopt u voedingsproducten online in België? Ontdek de FAVV-verplichtingen, HACCP-regels en hoe een erkende 3PL in Willebroek uw voedingslogistiek volledig conform beheert.',
    },
  }),
  makeArticle({
    id: 'externaliser-logistique-2026',
    category: 'logistics',
    readTime: 7,
    date: '2026-04-07',
    cover: { palette: 'accent', pattern: 'beam' },
    fr: {
      slug: 'externaliser-logistique-2026',
      title:
        'Externaliser la logistique e-commerce en 2026 : avantages et guide',
      excerpt:
        "Pourquoi externaliser la logistique de votre e-commerce en 2026 ? Découvrez les bénéfices, les économies et les risques — et comment Yaslan, votre partenaire 100 % belge, transforme votre supply-chain en levier de croissance.",
    },
    en: {
      slug: 'outsource-ecommerce-logistics-2026',
      title: 'Outsourcing e-commerce logistics in 2026: benefits and guide',
      excerpt:
        'Why outsource your e-commerce logistics in 2026? Discover the benefits, savings and risks — and how Yaslan, your 100 % Belgian partner, turns your supply chain into a growth lever.',
    },
    nl: {
      slug: 'logistiek-uitbesteden-ecommerce-2026',
      title: 'E-commercelogistiek uitbesteden in 2026: voordelen en gids',
      excerpt:
        'Waarom uw e-commercelogistiek uitbesteden in 2026? Ontdek de voordelen, besparingen en risico\'s — en hoe Yaslan, uw 100 % Belgische partner, uw supply chain omzet in een groeihefboom.',
    },
  }),
  makeArticle({
    id: 'ia-transforme-logistique',
    category: 'technology',
    readTime: 7,
    date: '2026-04-07',
    cover: { palette: 'ink', pattern: 'grid' },
    fr: {
      slug: 'ia-transforme-logistique',
      title:
        "Comment l'intelligence artificielle transforme la logistique e-commerce en 2025",
      excerpt:
        "Découvrez comment l'intelligence artificielle révolutionne la logistique e-commerce en 2025. Suivi intelligent, automatisation et optimisation des flux : Yaslan combine technologie et expertise humaine pour offrir une logistique plus rapide, fiable et flexible.",
    },
    en: {
      slug: 'how-ai-transforms-ecommerce-logistics',
      title: 'How artificial intelligence (AI) transforms e-commerce logistics in 2025',
      excerpt:
        'Discover how AI is revolutionising e-commerce logistics in 2025. Intelligent tracking, automation and flow optimisation: Yaslan combines technology and human expertise for faster, more reliable and flexible logistics.',
    },
    nl: {
      slug: 'hoe-ai-ecommerce-logistiek-transformeert',
      title: 'Hoe artificiële intelligentie (AI) e-commercelogistiek transformeert in 2025',
      excerpt:
        'Ontdek hoe AI de e-commercelogistiek revolutioneert in 2025. Intelligente tracking, automatisering en stroomoptimalisatie: Yaslan combineert technologie en menselijke expertise voor snellere, betrouwbaardere logistiek.',
    },
  }),
  makeArticle({
    id: '5-raison-externaliser-logistique',
    category: 'logistics',
    readTime: 5,
    date: '2026-04-07',
    cover: { palette: 'accent', pattern: 'package' },
    fr: {
      slug: '5-raison-externaliser-logistique',
      title: "5 bonnes raisons d'externaliser votre logistique e-commerce dès aujourd'hui",
      excerpt:
        "Externaliser votre logistique e-commerce réduit vos coûts fixes, libère votre temps et vous permet de vous concentrer sur la croissance. Découvrez les 5 raisons qui font la différence.",
    },
    en: {
      slug: '5-reasons-outsource-ecommerce-logistics',
      title: '5 good reasons to outsource your e-commerce logistics today',
      excerpt:
        'Outsourcing your e-commerce logistics reduces your fixed costs, frees up your time and lets you focus on growth. Discover the 5 reasons that make the difference.',
    },
    nl: {
      slug: '5-redenen-logistiek-uitbesteden',
      title: '5 goede redenen om uw e-commercelogistiek vandaag nog uit te besteden',
      excerpt:
        'Uw e-commercelogistiek uitbesteden verlaagt uw vaste kosten, maakt uw tijd vrij en laat u focussen op groei. Ontdek de 5 redenen die het verschil maken.',
    },
  }),
  makeArticle({
    id: '7-innovations-logistique',
    category: 'technology',
    readTime: 8,
    date: '2026-04-07',
    cover: { palette: 'ink', pattern: 'beam' },
    fr: {
      slug: '7-innovations-logistique',
      title:
        "7 innovations puissantes qui transforment le e-commerce (et comment Yaslan les met à votre service)",
      excerpt:
        "Découvrez comment la technologie logistique (automatisation, IA, suivi temps réel) révolutionne la logistique e-commerce et comment Yaslan en fait un levier de croissance pour votre boutique en ligne.",
    },
    en: {
      slug: '7-powerful-logistics-innovations-transforming-e-commerce',
      title: '7 powerful logistics innovations transforming e-commerce',
      excerpt:
        'Discover how logistics technology (automation, AI, real-time tracking) is revolutionising e-commerce logistics and how Yaslan turns it into a growth lever for your online shop.',
    },
    nl: {
      slug: '7-krachtige-logistieke-innovaties-die-e-commerce-transformeren',
      title: '7 krachtige logistieke innovaties die e-commerce transformeren',
      excerpt:
        'Ontdek hoe logistieke technologie (automatisering, AI, realtime tracking) e-commercelogistiek revolutioneert en hoe Yaslan dit inzet als groeihefboom voor uw webshop.',
    },
  }),
  makeArticle({
    id: 'revolution-logistique-ecommerce',
    category: 'logistics',
    readTime: 7,
    date: '2026-04-07',
    cover: { palette: 'ink', pattern: 'beam' },
    fr: {
      slug: 'revolution-logistique-ecommerce',
      title: 'La révolution de la logistique e-commerce',
      excerpt:
        "La logistique e-commerce se transforme en profondeur : maîtrise des coûts, flexibilité et modèles sans abonnement. Décryptage des tendances qui redéfinissent le secteur en 2026.",
    },
    en: {
      slug: 'ecommerce-logistics-revolution-2025',
      title: 'The E-commerce Logistics Revolution 2025',
      excerpt:
        'E-commerce logistics is transforming: cost control, flexibility, and subscription-free models. An in-depth look at the trends reshaping the sector in 2026.',
    },
    nl: {
      slug: 'ecommerce-logistieke-revolutie-2025',
      title: 'De E-commerce Logistieke Revolutie 2025',
      excerpt:
        'E-commercelogistiek ondergaat een grondige transformatie: kostenbeheer, flexibiliteit en abonnementsvrije modellen. Een diepgaande analyse van de trends die de sector in 2026 herdefiniëren.',
    },
  }),
  makeArticle({
    id: 'gestion-stock-ecommerce-logistique',
    category: 'logistics',
    readTime: 7,
    date: '2026-04-07',
    cover: { palette: 'accent', pattern: 'package' },
    fr: {
      slug: 'gestion-stock-ecommerce-logistique',
      title:
        'Stock e-commerce : éviter les ruptures et les surstocks grâce à une logistique bien organisée',
      excerpt:
        "Découvrez comment une logistique e-commerce bien organisée permet d'éviter ruptures et surstocks, d'améliorer la rotation des stocks et la rentabilité.",
    },
    en: {
      slug: 'e-commerce-stock-management-avoiding-stockouts-and-overstock',
      title: 'E-commerce stock: avoiding stockouts and overstock with well-organised logistics',
      excerpt:
        'Discover how well-organised e-commerce logistics helps avoid stockouts and overstock, improve stock rotation, and boost profitability.',
    },
    nl: {
      slug: 'e-commerce-voorraadbeheer-stockouts-en-overstock-vermijden',
      title: 'E-commercevoorraad: stockouts en overstock vermijden met goed georganiseerde logistiek',
      excerpt:
        'Ontdek hoe goed georganiseerde e-commercelogistiek stockouts en overstock helpt vermijden, de voorraadrotatie verbetert en de winstgevendheid verhoogt.',
    },
  }),
  makeArticle({
    id: 'delais-preparation-commande-ecommerce',
    category: 'logistics',
    readTime: 6,
    date: '2026-04-07',
    cover: { palette: 'accent', pattern: 'beam' },
    fr: {
      slug: 'delais-preparation-commande-ecommerce',
      title:
        'Délais de préparation e-commerce : pourquoi le cut-off logistique fait toute la différence',
      excerpt:
        "Découvrez pourquoi les délais de préparation et le cut-off logistique sont essentiels en e-commerce pour livrer plus vite et améliorer la satisfaction client.",
    },
    en: {
      slug: 'e-commerce-order-preparation-times-why-logistics-cut-off-matters',
      title: 'E-commerce preparation times: why logistics cut-off makes all the difference',
      excerpt:
        'Discover why order preparation times and the logistics cut-off are essential in e-commerce to deliver faster and improve customer satisfaction.',
    },
    nl: {
      slug: 'e-commerce-voorbereidingstijden-cut-off-logistiek',
      title: 'E-commerce voorbereidingstijden: waarom het logistieke cut-off moment alles verandert',
      excerpt:
        'Ontdek waarom voorbereidingstijden en het logistieke cut-off moment essentieel zijn in e-commerce om sneller te leveren en de klanttevredenheid te verbeteren.',
    },
  }),
  makeArticle({
    id: 'reduire-couts-expedition-ecommerce',
    category: 'delivery',
    readTime: 6,
    date: '2026-04-07',
    cover: { palette: 'accent', pattern: 'beam' },
    fr: {
      slug: 'reduire-couts-expedition-ecommerce',
      title: "Comment réduire les coûts d'expédition e-commerce sans ralentir les livraisons",
      excerpt:
        "Découvrez comment réduire les coûts d'expédition e-commerce grâce à une logistique optimisée, un choix intelligent de transporteurs et un 3PL flexible.",
    },
    en: {
      slug: 'how-to-reduce-e-commerce-shipping-costs-without-slowing-deliveries',
      title: 'How to reduce e-commerce shipping costs without slowing deliveries',
      excerpt:
        'Discover how to reduce e-commerce shipping costs through optimised logistics, smart carrier selection, and a flexible 3PL — without compromising delivery speed.',
    },
    nl: {
      slug: 'hoe-e-commerce-verzendkosten-verlagen-zonder-leveringen-te-vertragen',
      title: 'Hoe e-commerce verzendkosten verlagen zonder leveringen te vertragen',
      excerpt:
        'Ontdek hoe u e-commerce verzendkosten kunt verlagen via geoptimaliseerde logistiek, slimme vervoerderskeuze en een flexibele 3PL — zonder in te boeten op leveringssnelheid.',
    },
  }),
  makeArticle({
    id: 'logistique-avantage-concurrentiel-ecommerce',
    category: 'ecommerce',
    readTime: 6,
    date: '2026-04-07',
    cover: { palette: 'mixed', pattern: 'dots' },
    fr: {
      slug: 'logistique-avantage-concurrentiel-ecommerce',
      title: 'Pourquoi la logistique est devenue un avantage concurrentiel pour les marques e-commerce',
      excerpt:
        "Découvrez pourquoi la logistique est devenue un avantage concurrentiel majeur pour les marques e-commerce et comment un 3PL peut accélérer leur croissance.",
    },
    en: {
      slug: 'why-logistics-has-become-a-competitive-advantage-for-e-commerce-brands',
      title: 'Why logistics has become a competitive advantage for e-commerce brands',
      excerpt:
        'Discover why logistics has become a major competitive advantage for e-commerce brands and how a 3PL can accelerate their growth.',
    },
    nl: {
      slug: 'waarom-logistiek-een-concurrentievoordeel-is-geworden-voor-e-commercemerken',
      title: 'Waarom logistiek een concurrentievoordeel is geworden voor e-commercemerken',
      excerpt:
        'Ontdek waarom logistiek een belangrijk concurrentievoordeel is geworden voor e-commercemerken en hoe een 3PL hun groei kan versnellen.',
    },
  }),
  makeArticle({
    id: 'logistique-shopify-belgique',
    category: 'ecommerce',
    readTime: 5,
    date: '2026-04-07',
    cover: { palette: 'accent', pattern: 'grid' },
    fr: {
      slug: 'logistique-shopify-belgique',
      title: 'Logistique Shopify Belgique : connecter votre boutique à un 3PL',
      excerpt:
        "Vous gérez une boutique Shopify en Belgique ? Découvrez comment connecter Shopify à un prestataire 3PL pour automatiser votre logistique, réduire vos coûts et expédier sous 24 h.",
    },
    en: {
      slug: 'shopify-logistics-belgium-connect-your-store-to-a-3pl',
      title: 'Shopify logistics in Belgium: connecting your store to a 3PL',
      excerpt:
        'Running a Shopify store in Belgium? Discover how to connect Shopify to a 3PL provider to automate your logistics, reduce your costs, and ship within 24 h.',
    },
    nl: {
      slug: 'shopify-logistiek-belgie-winkel-koppelen-aan-3pl',
      title: 'Shopify-logistiek in België: uw winkel koppelen aan een 3PL',
      excerpt:
        'Beheert u een Shopify-winkel in België? Ontdek hoe u Shopify aan een 3PL-aanbieder koppelt om uw logistiek te automatiseren, kosten te verlagen en binnen 24u te verzenden.',
    },
  }),
  makeArticle({
    id: 'taxe-petits-colis-france-2026',
    category: 'delivery',
    readTime: 7,
    date: '2026-04-07',
    cover: { palette: 'mixed', pattern: 'beam' },
    fr: {
      slug: 'taxe-petits-colis-france-2026',
      title: 'Taxe petits colis France 2026 : 2 € par article — la solution logistique belge',
      excerpt:
        "La France impose une taxe de 2 € par colis sur les importations hors UE dès le 1er mars 2026. Découvrez comment les e-commerçants français peuvent optimiser leur logistique depuis la Belgique avec Yaslan.",
    },
    en: {
      slug: 'small-parcel-tax-france-2026-belgian-logistics-solution',
      title: 'France Small Parcel Tax 2026: 2 € per item — the Belgian logistics solution',
      excerpt:
        'France imposes a 2 € per item tax on non-EU imports from 1 March 2026. Discover how French e-merchants can optimise their logistics from Belgium with Yaslan.',
    },
    nl: {
      slug: 'kleine-pakketbelasting-frankrijk-2026-belgische-logistiek',
      title: 'Kleine pakketbelasting Frankrijk 2026: 2 € per artikel — de Belgische oplossing',
      excerpt:
        'Frankrijk legt een belasting van 2 € per artikel op niet-EU-invoer vanaf 1 maart 2026. Ontdek hoe Franse webshops hun logistiek vanuit België optimaliseren met Yaslan.',
    },
  }),

  // ─────────── 2025 articles ───────────
  makeArticle({
    id: 'prestataire-logistique-ecommerce',
    category: 'logistics',
    readTime: 7,
    date: '2025-12-07',
    cover: { palette: 'ink', pattern: 'package' },
    fr: {
      slug: 'prestataire-logistique-ecommerce',
      title:
        'Prestataire logistique e-commerce : le rôle clé du 3PL dans la croissance des boutiques en ligne',
      excerpt:
        "Prestataire logistique e-commerce : découvrez le rôle clé du 3PL pour accélérer votre croissance, réduire vos coûts et offrir une livraison rapide et fiable.",
    },
    en: {
      slug: 'ecommerce-logistics-provider-3pl-key-role-online-shop-growth',
      title: 'E-commerce Logistics Provider: the Key Role of the 3PL in Online Shop Growth',
      excerpt:
        'E-commerce logistics provider: discover the key role of the 3PL to accelerate your growth, reduce your costs and offer fast, reliable delivery.',
    },
    nl: {
      slug: 'ecommerce-logistieke-aanbieder-3pl-sleutelrol-webshop-groei',
      title: 'E-commerce logistieke aanbieder: de sleutelrol van de 3PL in webshopgroei',
      excerpt:
        'E-commerce logistieke aanbieder: ontdek de sleutelrol van de 3PL om uw groei te versnellen, uw kosten te verlagen en snelle, betrouwbare levering aan te bieden.',
    },
  }),
  makeArticle({
    id: 'externalisation-logistique-ecommerce',
    category: 'ecommerce',
    readTime: 6,
    date: '2025-12-07',
    cover: { palette: 'accent', pattern: 'dots' },
    fr: {
      slug: 'externalisation-logistique-ecommerce',
      title: 'Externalisation logistique e-commerce : à partir de quand est-ce rentable pour une TPE / PME ?',
      excerpt:
        "Externalisation logistique e-commerce : découvrez à partir de quel volume une TPE/PME gagne en rentabilité et accélère sa croissance grâce au 3PL.",
    },
    en: {
      slug: 'ecommerce-logistics-outsourcing-when-profitable-small-business-sme',
      title: 'E-commerce Logistics Outsourcing: When Is It Profitable for a Small Business / SME?',
      excerpt:
        'E-commerce logistics outsourcing: discover from what volume a small business / SME gains profitability and accelerates growth with a 3PL.',
    },
    nl: {
      slug: 'ecommerce-logistiek-uitbesteden-wanneer-rendabel-kmo',
      title: 'E-commerce logistiek uitbesteden: vanaf wanneer is het rendabel voor een kmo?',
      excerpt:
        'E-commerce logistiek uitbesteden: ontdek vanaf welk volume een kmo / kleine onderneming rendabiliteit wint en de groei versnelt dankzij 3PL.',
    },
  }),
  makeArticle({
    id: 'logistique-ecommerce-marques-mode',
    category: 'ecommerce',
    readTime: 6,
    date: '2025-12-07',
    cover: { palette: 'accent', pattern: 'package' },
    fr: {
      slug: 'logistique-ecommerce-marques-mode',
      title:
        'Logistique e-commerce pour marques de mode : gérer tailles, retours et saisonnalité avec efficacité',
      excerpt:
        "Logistique e-commerce mode : découvrez comment gérer tailles, retours et saisonnalité pour des envois rapides, fiables et adaptés aux marques fashion.",
    },
    en: {
      slug: 'ecommerce-logistics-fashion-brands-sizes-returns-seasonality',
      title: 'E-commerce Logistics for Fashion Brands: Managing Sizes, Returns and Seasonality',
      excerpt:
        'Fashion e-commerce logistics: discover how to manage sizes, returns and seasonality for fast, reliable shipments tailored to fashion brands.',
    },
    nl: {
      slug: 'ecommerce-logistiek-modemerken-maten-retourzendingen-seizoensgebondenheid',
      title: 'E-commercelogistiek voor modemerken: maten, retourzendingen en seizoensgebondenheid',
      excerpt:
        'Modelogistiek e-commerce: ontdek hoe u maten, retourzendingen en seizoensgebondenheid beheert voor snelle, betrouwbare zendingen voor modemerken.',
    },
  }),
  makeArticle({
    id: 'logistique-ecommerce-willebroek',
    category: 'logistics',
    readTime: 6,
    date: '2025-12-05',
    cover: { palette: 'mixed', pattern: 'grid' },
    fr: {
      slug: 'logistique-ecommerce-willebroek',
      title:
        'Logistique e-commerce à Willebroek : pourquoi cette zone devient la nouvelle capitale du fulfillment',
      excerpt:
        "Logistique e-commerce Willebroek : découvrez pourquoi cette zone devient la capitale belge du fulfillment et comment expédier plus vite partout en Europe.",
    },
    en: {
      slug: 'ecommerce-logistics-willebroek-belgiums-new-fulfilment-hub',
      title: "E-commerce Logistics Willebroek: Belgium's New Fulfilment Capital",
      excerpt:
        'E-commerce logistics Willebroek: discover why this area is becoming the Belgian capital of fulfilment and how to ship faster across Europe.',
    },
    nl: {
      slug: 'ecommerce-logistiek-willebroek-nieuwe-fulfilment-hoofdstad',
      title: 'E-commercelogistiek Willebroek: nieuwe fulfilmenthoofdstad van België',
      excerpt:
        'E-commercelogistiek Willebroek: ontdek waarom dit gebied de Belgische hoofdstad van fulfilment wordt en hoe u sneller in heel Europa verzendt.',
    },
  }),
  // Consolidated 2026-08-31: absorbed `tarifs-logistique-e-commerce`, which targeted
  // the same "what does a 3PL cost" intent and carried a CONTRADICTING price range
  // (pick & pack 3-7 EUR here vs 1-4.50 EUR there). The two articles held 27,922
  // impressions for 12 clicks between them. One reconciled table now, plus the
  // subscription-model section that was the other article's only unique material.
  makeArticle({
    id: 'cout-logistique-e-commerce',
    category: 'ecommerce',
    readTime: 10,
    date: '2025-12-05',
    dateModified: '2026-08-31',
    cover: { palette: 'accent', pattern: 'dots' },
    fr: {
      slug: 'cout-logistique-e-commerce',
      title: "Prix d'un 3PL en Belgique : combien coûte la logistique e-commerce en 2026",
      seoTitle: "Prix d'un 3PL en Belgique : les tarifs 2026",
      excerpt:
        "Combien coûte la logistique e-commerce en Belgique en 2026 : fourchettes de prix par poste, comment lire un devis 3PL et les frais qui n'y figurent pas.",
    },
    en: {
      slug: 'ecommerce-logistics-cost-belgium-rates-models-benchmarks',
      title: '3PL pricing in Belgium: what e-commerce logistics costs in 2026',
      seoTitle: '3PL pricing in Belgium: the 2026 rates',
      excerpt:
        'What e-commerce logistics costs in Belgium in 2026: price ranges per line item, how to read a 3PL quote, and the fees that never appear on it.',
    },
    nl: {
      slug: 'ecommerce-logistiek-kosten-belgie-tarieven-modellen',
      title: 'Prijs van een 3PL in België: wat e-commercelogistiek kost in 2026',
      seoTitle: 'Prijs van een 3PL in België: tarieven 2026',
      excerpt:
        'Wat e-commercelogistiek kost in België in 2026: prijsvorken per post, hoe u een 3PL-offerte leest, en de kosten die er nooit in staan.',
    },
  }),
  makeArticle({
    id: 'comment-choisir-partenaire-logistique',
    category: 'logistics',
    readTime: 7,
    date: '2025-12-04',
    cover: { palette: 'ink', pattern: 'grid' },
    fr: {
      slug: 'comment-choisir-partenaire-logistique',
      title:
        'Comment choisir son partenaire logistique e-commerce : 7 critères essentiels pour ne pas se tromper',
      excerpt:
        "Découvrez comment choisir le bon partenaire logistique e-commerce grâce à 7 critères clés : expertise, rapidité, technologie, coûts et qualité de service.",
    },
    en: {
      slug: 'how-to-choose-e-commerce-logistics-partner',
      title: 'How to choose your e-commerce logistics partner: 7 essential criteria',
      excerpt:
        'Discover how to choose the right e-commerce logistics partner with 7 key criteria: expertise, speed, technology, costs, and service quality.',
    },
    nl: {
      slug: 'hoe-kies-je-e-commerce-logistieke-partner',
      title: 'Hoe kies je je e-commerce logistieke partner: 7 essentiële criteria',
      excerpt:
        'Ontdek hoe u de juiste e-commerce logistieke partner kiest aan de hand van 7 sleutelcriteria: expertise, snelheid, technologie, kosten en servicekwaliteit.',
    },
  }),
  makeArticle({
    id: 'qu-est-ce-que-le-e-fulfillment',
    category: 'logistics',
    readTime: 9,
    date: '2025-12-04',
    cover: { palette: 'accent', pattern: 'package' },
    fr: {
      slug: 'qu-est-ce-que-le-e-fulfillment',
      title:
        "Qu'est-ce que le e-fulfillment ? 7 clés essentielles pour comprendre cette logistique e-commerce stratégique",
      excerpt:
        "e-fulfillment : découvrez une définition simple, les étapes clés, la différence avec la logistique classique et pourquoi le e-fulfillment est devenu indispensable pour tout site e-commerce.",
    },
    en: {
      slug: 'what-is-e-fulfilment-guide',
      title: 'What is e-fulfilment? 7 essential keys to understand e-commerce logistics',
      excerpt:
        'E-fulfilment: a simple definition, key stages, the difference from classic logistics, and why e-fulfilment is indispensable for any e-commerce site.',
    },
    nl: {
      slug: 'wat-is-e-fulfilment-gids',
      title: 'Wat is e-fulfilment? 7 essentiële sleutels tot e-commercelogistiek',
      excerpt:
        'E-fulfilment: een eenvoudige definitie, de sleutelstappen, het verschil met klassieke logistiek en waarom e-fulfilment onmisbaar is voor elke webshop.',
    },
  }),
  makeArticle({
    id: 'logistique-verte-yaslan',
    category: 'sustainability',
    readTime: 7,
    date: '2025-12-03',
    cover: { palette: 'mixed', pattern: 'waves' },
    fr: {
      slug: 'logistique-verte-yaslan',
      title:
        "Logistique verte en e-commerce : comment réduire l'empreinte carbone de vos livraisons",
      excerpt:
        "Découvrez comment adopter une logistique verte en e-commerce. Yaslan, prestataire belge, combine technologie, efficacité et engagement durable pour réduire l'empreinte carbone de vos livraisons.",
    },
    en: {
      slug: 'green-logistics-ecommerce-yaslan',
      title: 'Green logistics in e-commerce: cutting your delivery carbon footprint',
      excerpt:
        'Discover how to adopt green e-commerce logistics. Yaslan, a Belgian provider, combines technology, efficiency and sustainable commitment to reduce the carbon footprint of your deliveries.',
    },
    nl: {
      slug: 'groene-logistiek-ecommerce-yaslan',
      title: 'Groene logistiek in e-commerce: de CO₂-voetafdruk van je leveringen verkleinen',
      excerpt:
        'Ontdek hoe je groene e-commercelogistiek toepast. Yaslan, een Belgische dienstverlener, combineert technologie, efficiëntie en duurzaam engagement om de CO₂-voetafdruk van je leveringen te verkleinen.',
    },
  }),
  makeArticle({
    id: 'livraison-e-commerce-2025',
    category: 'delivery',
    readTime: 7,
    date: '2025-12-03',
    cover: { palette: 'accent', pattern: 'beam' },
    fr: {
      slug: 'livraison-e-commerce-2025',
      title:
        'Livraison e-commerce en 2025 : attentes clients, défis logistiques et comment Yaslan garantit un service optimal',
      excerpt:
        "Découvrez les tendances 2025 de la livraison e-commerce : rapidité, fiabilité et durabilité. Yaslan, prestataire belge, offre une logistique flexible et locale pour une expérience client parfaite.",
    },
    en: {
      slug: 'ecommerce-delivery-2025-yaslan',
      title: 'E-commerce delivery in 2025: customer expectations, logistics challenges and how Yaslan delivers',
      excerpt:
        'Discover the 2025 trends in e-commerce delivery: speed, reliability and sustainability. Yaslan, a Belgian provider, offers flexible local logistics for a perfect customer experience.',
    },
    nl: {
      slug: 'ecommerce-levering-2025-yaslan',
      title: 'E-commercelevering in 2025: klantverwachtingen, logistieke uitdagingen en hoe Yaslan het waarmaakt',
      excerpt:
        'Ontdek de trends van 2025 in e-commercelevering: snelheid, betrouwbaarheid en duurzaamheid. Yaslan, een Belgische dienstverlener, biedt flexibele lokale logistiek voor een perfecte klantervaring.',
    },
  }),
  makeArticle({
    id: 'tendances-e-commerce-2025',
    category: 'ecommerce',
    readTime: 8,
    date: '2025-12-03',
    cover: { palette: 'mixed', pattern: 'dots' },
    fr: {
      slug: 'tendances-e-commerce-2025',
      title: 'E-commerce en 2025 : tendances majeures et comment Yaslan booste vos ventes en ligne',
      excerpt:
        "Découvrez les tendances 2025 du e-commerce — personnalisation, SEO, expérience client, logistique — et comment Yaslan accompagne votre croissance en optimisant la supply chain.",
    },
    en: {
      slug: 'ecommerce-trends-2025-yaslan',
      title: 'E-commerce in 2025: major trends and how Yaslan boosts your online sales',
      excerpt:
        'Discover the 2025 e-commerce trends — personalisation, SEO, customer experience, logistics — and how Yaslan supports your growth by optimising your supply chain.',
    },
    nl: {
      slug: 'ecommerce-trends-2025-yaslan',
      title: 'E-commerce in 2025: grote trends en hoe Yaslan je online verkoop stimuleert',
      excerpt:
        'Ontdek de e-commercetrends van 2025 — personalisatie, SEO, klantervaring, logistiek — en hoe Yaslan je groei ondersteunt door je supply chain te optimaliseren.',
    },
  }),
]

/** Slug localisé d'un article pour une langue donnée. */
export function articleSlug(a: Article, locale: Locale): string {
  return a.i18n[locale].slug
}

/** Résout un article par son slug localisé (langue + slug). */
export function getArticleByLocaleSlug(
  locale: Locale,
  slug: string,
): Article | undefined {
  return articles.find((a) => a.i18n[locale].slug === slug)
}
